import React, { useEffect, useState } from 'react';
import { theme, alpha } from '../theme';
import { useResponsive } from '../hooks/useResponsive';
import { apiRequest } from '../utils/apiUtils';

const POSITIONS = ['Arquero', 'Defensor', 'Mediocampista', 'Delantero'] as const;
type PositionName = (typeof POSITIONS)[number];

interface Props {
  isOpen: boolean;
  matchId: string;
  onClose: () => void;
  onApplied: () => void;
}

const ApplyToPublicMatchModal: React.FC<Props> = ({ isOpen, matchId, onClose, onApplied }) => {
  const { isMobile } = useResponsive();
  const [message, setMessage] = useState('');
  const [position, setPosition] = useState<PositionName | null>(null);
  const [skill, setSkill] = useState<string>('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setMessage('');
      setPosition(null);
      setSkill('');
      setSending(false);
      setError(null);
      setSuccess(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const trimmed = message.trim();
  const msgOk = trimmed.length >= 3;
  const canSubmit = msgOk && !sending;

  const submit = async () => {
    if (!msgOk) {
      setError('Escribí un mensaje para el organizador (mín. 3 caracteres).');
      return;
    }
    setSending(true);
    setError(null);
    const body: Record<string, unknown> = { message: trimmed };
    if (position) body.preferredPosition = position;
    if (skill) {
      const s = parseInt(skill, 10);
      if (s >= 1 && s <= 5) body.selfRatedSkill = s;
    }
    const res = await apiRequest(`/matches/${matchId}/join-applications`, {
      method: 'POST',
      body,
    });
    setSending(false);
    if (res.error) {
      setError(res.error);
      return;
    }
    setSuccess(true);
    onApplied();
    setTimeout(() => onClose(), 1100);
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 12px',
    background: theme.colors.background,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: 10,
    color: theme.colors.text,
    fontSize: 14,
    fontFamily: theme.fonts.body,
    boxSizing: 'border-box',
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.55)',
        zIndex: 2000,
        display: 'flex',
        alignItems: isMobile ? 'flex-end' : 'center',
        justifyContent: 'center',
        padding: isMobile ? 0 : 16,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: theme.colors.surface,
          width: '100%',
          maxWidth: 520,
          borderRadius: isMobile ? '20px 20px 0 0' : 18,
          padding: '20px 22px 22px',
          maxHeight: isMobile ? '92vh' : '90vh',
          overflowY: 'auto',
          boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 20,
              background: alpha('primary', '20'),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: theme.colors.primary,
              fontSize: 22,
              flexShrink: 0,
            }}
          >
            ✋
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2
              style={{
                fontFamily: theme.fonts.heading,
                color: theme.colors.text,
                margin: 0,
                fontSize: 18,
                fontWeight: 800,
              }}
            >
              Postularme
            </h2>
            <p
              style={{
                margin: '4px 0 0',
                color: theme.colors.textSecondary,
                fontSize: 13,
              }}
            >
              El organizador recibe tu mensaje y te confirma si hay lugar.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            style={{
              background: theme.colors.background,
              border: 'none',
              borderRadius: '50%',
              width: 32,
              height: 32,
              fontSize: 18,
              cursor: 'pointer',
              color: theme.colors.textSecondary,
            }}
          >
            ✕
          </button>
        </div>

        {success ? (
          <div
            style={{
              padding: 18,
              background: alpha('success', '20'),
              border: `1px solid ${theme.colors.success}`,
              borderRadius: 12,
              textAlign: 'center',
            }}
          >
            <div style={{ fontSize: 26, marginBottom: 6 }}>✅</div>
            <div style={{ fontWeight: 800, color: theme.colors.success }}>
              Postulación enviada
            </div>
            <div style={{ color: theme.colors.textSecondary, fontSize: 13, marginTop: 4 }}>
              El organizador te avisará por notificación.
            </div>
          </div>
        ) : (
          <>
            <label
              htmlFor="apply-msg"
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: theme.colors.textSecondary,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                marginBottom: 6,
                display: 'block',
              }}
            >
              Mensaje
            </label>
            <textarea
              id="apply-msg"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Hola, me sumaría como…"
              maxLength={600}
              rows={4}
              style={{ ...inputStyle, resize: 'vertical', fontFamily: theme.fonts.body }}
            />
            <div style={{ fontSize: 11, color: theme.colors.textSecondary, marginTop: 4 }}>
              {trimmed.length < 3
                ? `Mínimo 3 caracteres · ${trimmed.length}/3`
                : `${message.length}/600`}
            </div>

            <label
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: theme.colors.textSecondary,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                marginTop: 16,
                marginBottom: 6,
                display: 'block',
              }}
            >
              Posición preferida <span style={{ opacity: 0.6 }}>(opcional)</span>
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {POSITIONS.map((p) => {
                const selected = position === p;
                return (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setPosition(selected ? null : p)}
                    style={{
                      padding: '8px 14px',
                      borderRadius: 999,
                      border: `1px solid ${selected ? theme.colors.primary : theme.colors.border}`,
                      background: selected ? alpha('primary', '22') : theme.colors.background,
                      color: selected ? theme.colors.primary : theme.colors.text,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    {p}
                  </button>
                );
              })}
            </div>

            <label
              htmlFor="apply-skill"
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: theme.colors.textSecondary,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                marginTop: 16,
                marginBottom: 6,
                display: 'block',
              }}
            >
              Auto-evaluación <span style={{ opacity: 0.6 }}>(opcional)</span>
            </label>
            <select
              id="apply-skill"
              value={skill}
              onChange={(e) => setSkill(e.target.value)}
              style={inputStyle}
            >
              <option value="">No indicar</option>
              <option value="1">1 — Principiante</option>
              <option value="2">2 — Básico</option>
              <option value="3">3 — Intermedio</option>
              <option value="4">4 — Avanzado</option>
              <option value="5">5 — Experto</option>
            </select>

            {error && (
              <div
                style={{
                  marginTop: 14,
                  padding: '10px 12px',
                  background: alpha('danger', '20'),
                  border: `1px solid ${theme.colors.danger}`,
                  borderRadius: 10,
                  color: theme.colors.danger,
                  fontSize: 13,
                }}
              >
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
              <button
                type="button"
                onClick={onClose}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: theme.colors.background,
                  color: theme.colors.text,
                  border: `1px solid ${theme.colors.border}`,
                  borderRadius: 10,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={!canSubmit}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: canSubmit ? theme.colors.primary : theme.colors.border,
                  color: theme.colors.background,
                  border: 'none',
                  borderRadius: 10,
                  fontWeight: 800,
                  cursor: canSubmit ? 'pointer' : 'not-allowed',
                  fontFamily: theme.fonts.heading,
                }}
              >
                {sending ? 'Enviando…' : 'Enviar postulación'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ApplyToPublicMatchModal;
